import { Link } from 'react-router-dom';
import {
  ArrowRight, CheckCircle, ShoppingBag, Building2, Store, Monitor,
  Home, Briefcase, Truck, Layers, Ship
} from 'lucide-react';
import { FadeInUp } from '../components/animations';
import SEO from '../components/SEO';
import { breadcrumbSchema } from '../utils/schema';

const ForBuyersPage = () => {
  const buyerTypes = [
    { icon: <ShoppingBag size={24} />, label: 'Fashion Brands' },
    { icon: <Ship size={24} />, label: 'Importers' },
    { icon: <Store size={24} />, label: 'Wholesalers' },
    { icon: <Building2 size={24} />, label: 'Retailers' },
    { icon: <Monitor size={24} />, label: 'E-commerce Brands' },
    { icon: <Layers size={24} />, label: 'Private Label Businesses' },
    { icon: <Home size={24} />, label: 'Hospitality Companies' },
    { icon: <Briefcase size={24} />, label: 'Interior & Project Buyers' },
    { icon: <Truck size={24} />, label: 'Distributors' },
  ];

  const steps = [
    { num: '01', title: 'Share Requirement', desc: 'Tell us what you need \u2014 product, quantity, quality, pricing and timeline.' },
    { num: '02', title: 'We Identify Manufacturers', desc: 'We find suitable Indian manufacturers based on your specific requirements.' },
    { num: '03', title: 'Receive Options & Quotations', desc: 'Compare options, pricing and capabilities from shortlisted manufacturers.' },
    { num: '04', title: 'Sample Development', desc: 'We coordinate sample production and manage approval communication.' },
    { num: '05', title: 'Production Coordination', desc: 'End-to-end production follow-up with regular updates and timelines.' },
    { num: '06', title: 'Quality Follow-Up', desc: 'Quality inspection and checks at key production stages.' },
    { num: '07', title: 'Shipment Coordination', desc: 'Packing, documentation and shipment coordination to your destination.' },
  ];

  return (
    <div className="pt-20" data-testid="for-buyers-page">
      <SEO
        title="For International Buyers | Source from India | Taiga Sourcing"
        description="International buyers — source products from India with one sourcing partner. Taiga Sourcing coordinates manufacturers, quotations, production and shipment."
        keywords="India buying agent, source from India, India sourcing for buyers, international buyer India"
        path="/for-buyers"
        schema={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'For Buyers', path: '/for-buyers' }])}
      />
      <section className="relative py-24 lg:py-32 bg-[#071E45] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <p className="text-sm tracking-[0.3em] uppercase text-white/50 mb-4 font-medium">For International Buyers</p>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.1] mb-6" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="buyers-title">
              BRING YOUR REQUIREMENT.<br />
              WE'LL FIND THE RIGHT INDIA SOURCING ROUTE.
            </h1>
            <p className="text-base text-white/70 leading-relaxed max-w-3xl mx-auto">
              Instead of managing multiple manufacturers and suppliers yourself, work with one sourcing partner who coordinates the process in India.
            </p>
          </FadeInUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FadeInUp className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              WHO WE WORK WITH
            </h2>
          </FadeInUp>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-5">
            {buyerTypes.map((bt, i) => (
              <FadeInUp key={i} delay={i * 0.05}>
                <div className="flex items-center gap-4 p-5 bg-[#fafaf9] border border-[#E5E0D8]/60">
                  <div className="text-[#1E3D66] flex-shrink-0">{bt.icon}</div>
                  <span className="text-sm font-semibold text-[#0B2A57]">{bt.label}</span>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#fafaf9]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <FadeInUp className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0B2A57] mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              HOW IT WORKS FOR BUYERS
            </h2>
          </FadeInUp>
          <div className="space-y-6">
            {steps.map((step, i) => (
              <FadeInUp key={i} delay={i * 0.05}>
                <div className="flex items-start gap-6 p-6 bg-white border border-[#E5E0D8]/60">
                  <span className="text-3xl font-bold text-[#0B2A57]/15 flex-shrink-0" style={{ fontFamily: 'Cabinet Grotesk' }}>{step.num}</span>
                  <div>
                    <h3 className="text-sm font-semibold tracking-wider uppercase text-[#0B2A57] mb-1">{step.title}</h3>
                    <p className="text-sm text-[#6B7280] leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#071E45] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <FadeInUp>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk' }}>
              READY TO START?
            </h2>
            <p className="text-base text-white/60 leading-relaxed mb-8">
              Share your product requirement and let us find the right sourcing solution in India.
            </p>
            <Link
              to="/request-sourcing"
              className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#0B2A57] text-sm font-semibold tracking-wider uppercase transition-all hover:shadow-xl hover:-translate-y-0.5"
              data-testid="buyers-cta"
            >
              Request Sourcing <ArrowRight size={18} />
            </Link>
          </FadeInUp>
        </div>
      </section>
    </div>
  );
};

export default ForBuyersPage;
